import {
    CREATE_SELLER,
    GET_SELLER,
    GET_SELLERS,
    GET_SELLER_SUMMARY,
    UPDATE_SELLER,
    WITHDRAW_SELLER_WALLET,
} from '@/graphql/queries/sellers.graphql';
import { apolloClient } from './apollo-client';

import type { MutationCreateSellerArgs, MutationUpdateSellerArgs, SellerOrderBy, SortOrder } from '@/graphql/graphql';

const sellersSortFieldMap: Record<string, keyof SellerOrderBy> = {
    sku: 'sku',
    legalName: 'legalName',
    contactName: 'contactName',
    contactEmail: 'contactEmail',
};

export type FetchSellersListArgs = {
    page: number;
    pageSize: number;
    filters?: {
        [key: string]: unknown;
    };
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
};

export async function fetchSellersList({ page, pageSize, filters, sortBy, sortOrder }: FetchSellersListArgs) {
    const sortObj: SellerOrderBy = {};

    if (sortBy && sellersSortFieldMap[sortBy]) {
        sortObj[sellersSortFieldMap[sortBy]] = sortOrder as SortOrder;
    }

    const response = await apolloClient.query({
        query: GET_SELLERS,
        variables: {
            limit: pageSize,
            offset: (page - 1) * pageSize,
            orderBy: sortObj,
            contactEmail: filters?.contactEmail,
            contactName: filters?.contactName,
            sku: filters?.sku && Number(filters?.sku),
            legalName: filters?.legalName,
        },
    });

    return { data: response.data.searchSellers.sellers, total: response.data.searchSellers.count };
}

export async function updateSeller(input: MutationUpdateSellerArgs) {
    const result = await apolloClient.mutate({
        mutation: UPDATE_SELLER,
        variables: {
            id: input.id,
            billingEmail: input.billingEmail?.toLocaleLowerCase(),
            businessProviderId: input.businessProviderId,
            commissionRate: input.commissionRate,
            contactEmail: input.contactEmail?.toLocaleLowerCase(),
            contactName: input.contactName,
            payoutTemplate: input.payoutTemplate,
            legalName: input.legalName,
            postalAddress: input.postalAddress,
            vatId: input.vatId,
            cognitoPassword: input.cognitoPassword,
        },
    });

    return { data: result };
}

export async function createSeller(input: MutationCreateSellerArgs) {
    const result = await apolloClient.mutate({
        mutation: CREATE_SELLER,
        variables: {
            billingEmail: input.billingEmail?.toLocaleLowerCase(),
            businessProviderId: input.businessProviderId,
            commissionRate: input.commissionRate,
            contactEmail: input.contactEmail?.toLocaleLowerCase(),
            contactName: input.contactName,
            payoutTemplate: input.payoutTemplate,
            legalName: input.legalName,
            postalAddress: input.postalAddress,
            vatId: input.vatId,
        },
    });

    return { data: result };
}

export async function getSeller(id: string, full = false) {
    const response = await apolloClient.query({
        query: full ? GET_SELLER : GET_SELLER_SUMMARY,
        variables: {
            id: id,
        },
    });

    return response.data.seller;
}

export async function withdrawSellerWallet(sellerId: string) {
    const result = await apolloClient.mutate({
        mutation: WITHDRAW_SELLER_WALLET,
        variables: {
            sellerId: sellerId,
        },
    });

    return result.data;
}
